import { useMemo, useCallback } from 'react'

import type { DiscountScope } from '../../../gql/graphql'

import type { PlanCalculation } from '../types'
import {
  getCustomerDiscounts,
  getSubscriptionDiscounts,
  calculateTotalDiscountValue,
  applyDiscountsToCalculation,
} from '../utils/discountCalculations'
import type {
  DiscountedPlanCalculation,
  QueryDiscount,
} from '../utils/discountCalculations'

/**
 * Configuration for discount calculation hook
 */
export interface UseDiscountCalculationsProps {
  /** Array of available discounts to apply */
  discounts: QueryDiscount[]
}

/**
 * Return type for discount calculation operations
 */
export interface UseDiscountCalculationsReturn {
  /** Apply all relevant discounts to a base price calculation */
  applyDiscounts: (
    baseCalculation: PlanCalculation,
    scope?: DiscountScope,
  ) => DiscountedPlanCalculation
  /** Get all customer-level discounts */
  getCustomerDiscounts: () => QueryDiscount[]
  /** Get all subscription-level discounts */
  getSubscriptionDiscounts: () => QueryDiscount[]
  /** Calculate total discount value for a given price */
  calculateTotalDiscountValue: (
    basePrice: number,
    scope?: DiscountScope,
  ) => number
}

/**
 * Custom hook for handling discount calculations on plan pricing.
 *
 * This hook provides utilities to:
 * - Apply multiple discounts in the correct order (by ordinal)
 * - Handle both percentage and amount-based discounts
 * - Support customer vs subscription level discounts
 * - Calculate cumulative discount effects
 *
 * @param props - Configuration object containing available discounts
 * @returns Object with discount calculation functions
 *
 * @example
 * ```typescript
 * const { applyDiscounts } = useDiscountCalculations({ discounts: planDiscounts })
 *
 * const baseCalculation = { basePrice: 100, totalPrice: 100, pricePerChannel: 10 }
 * const discountedCalculation = applyDiscounts(baseCalculation, DiscountScope.customer)
 * ```
 */
export function useDiscountCalculations({
  discounts,
}: UseDiscountCalculationsProps): UseDiscountCalculationsReturn {
  // Memoize customer-level discounts
  const customerDiscounts = useMemo(
    () => getCustomerDiscounts(discounts),
    [discounts],
  )

  // Memoize subscription-level discounts
  const subscriptionDiscounts = useMemo(
    () => getSubscriptionDiscounts(discounts),
    [discounts],
  )

  /**
   * Get customer-level discounts
   */
  const getCustomerDiscountsCallback = useCallback(
    () => customerDiscounts,
    [customerDiscounts],
  )

  /**
   * Get subscription-level discounts
   */
  const getSubscriptionDiscountsCallback = useCallback(
    () => subscriptionDiscounts,
    [subscriptionDiscounts],
  )

  /**
   * Calculate the total discount value for a given base price
   */
  const calculateTotalDiscountValueCallback = useCallback(
    (basePrice: number, scope?: DiscountScope): number => {
      return calculateTotalDiscountValue(basePrice, discounts, scope)
    },
    [discounts],
  )

  /**
   * Apply all relevant discounts to a base calculation
   */
  const applyDiscounts = useCallback(
    (
      baseCalculation: PlanCalculation,
      scope?: DiscountScope,
    ): DiscountedPlanCalculation => {
      return applyDiscountsToCalculation(baseCalculation, discounts, scope)
    },
    [discounts],
  )

  return {
    applyDiscounts,
    getCustomerDiscounts: getCustomerDiscountsCallback,
    getSubscriptionDiscounts: getSubscriptionDiscountsCallback,
    calculateTotalDiscountValue: calculateTotalDiscountValueCallback,
  }
}
